import React, { useCallback, useEffect, useState } from 'react';
import { Mail, RefreshCw, CheckCircle, XCircle, ShieldCheck, ShieldAlert, Loader2, Lightbulb, PartyPopper, Stethoscope } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { adminGet, adminPut, adminPost, reportError } from './adminApi';

const EMPTY_SMTP = { smtp_host: '', smtp_port: 587, smtp_user: '', smtp_pass: '', from_email: '' };

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '');

/** Health banner from the send log: are e-mails flowing right now? */
function HealthBanner({ health }) {
  if (!health) return null;
  if (!health.configured) {
    return <div className="rounded-lg border border-border bg-secondary/60 px-3 py-2 text-sm flex items-start gap-2 mb-4" data-testid="smtp-health" data-status="unconfigured"><ShieldAlert className="h-4 w-4 mt-0.5 text-muted-foreground" /> <span>Aucun serveur SMTP configuré : les alertes, rappels et récapitulatifs ne peuvent pas partir.</span></div>;
  }
  if (health.status === 'broken') {
    return (
      <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2.5 text-sm flex items-start gap-2 mb-4" role="alert" data-testid="smtp-health" data-status="broken">
        <ShieldAlert className="h-4 w-4 mt-0.5 text-destructive flex-shrink-0" />
        <div>
          <p className="font-semibold text-destructive">Les e-mails ne partent pas actuellement</p>
          <p className="text-xs text-muted-foreground mt-0.5">Dernière erreur le {fmt(health.last_error?.at)} : {health.last_error?.error}{health.errors_24h ? ` · ${health.errors_24h} échec${health.errors_24h > 1 ? 's' : ''} sur 24 h` : ''}{health.last_success ? ` · dernier envoi réussi le ${fmt(health.last_success)}` : ' · aucun envoi réussi enregistré'}.</p>
          <p className="text-xs mt-1">Lancez le <strong>diagnostic</strong> ci-dessous : il indique l'étape qui bloque et comment la corriger.</p>
        </div>
      </div>
    );
  }
  if (health.status === 'ok') {
    return (
      <div className="rounded-lg border border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/10 px-3 py-2.5 text-sm flex items-start gap-2 mb-4" data-testid="smtp-health" data-status="ok">
        <ShieldCheck className="h-4 w-4 mt-0.5 text-[hsl(var(--primary))] flex-shrink-0" />
        <div>
          <p className="font-semibold text-[hsl(var(--primary))]">E-mails opérationnels{health.recovered ? ' — rétablis' : ''}</p>
          <p className="text-xs text-muted-foreground mt-0.5">Dernier envoi réussi le {fmt(health.last_success)}{health.last_error ? ` · dernière erreur le ${fmt(health.last_error.at)}` : ''}.</p>
        </div>
      </div>
    );
  }
  return <div className="rounded-lg border border-border bg-secondary/60 px-3 py-2 text-sm flex items-start gap-2 mb-4" data-testid="smtp-health" data-status="unknown"><Mail className="h-4 w-4 mt-0.5 text-muted-foreground" /> <span>Aucun envoi enregistré pour l'instant : lancez un test pour vérifier la configuration.</span></div>;
}

/** Step-by-step diagnosis result (DNS → connexion → chiffrement → authentification → envoi). */
function TestResult({ result, wasBroken }) {
  if (!result) return null;
  const ok = result.success;
  return (
    <div data-testid="smtp-test-result" data-code={result.code} className={`mt-3 text-sm rounded-lg px-3 py-3 border ${ok ? 'bg-[hsl(var(--primary))]/10 border-[hsl(var(--primary))]/30' : 'bg-destructive/10 border-destructive/30'}`}>
      <p className={`font-semibold flex items-center gap-1.5 ${ok ? 'text-[hsl(var(--primary))]' : 'text-destructive'}`}>
        {ok ? <PartyPopper className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
        {ok ? (wasBroken ? 'Rétabli : les e-mails partent de nouveau' : 'Tout fonctionne') : result.error}
      </p>
      <p className="text-xs text-muted-foreground mt-1" data-testid="smtp-test-explanation">{ok ? `Email de test envoyé à ${result.to}. Vérifiez la boîte de réception (et les spams). ` : ''}{result.explanation}</p>
      {result.steps?.length > 0 && (
        <ol className="mt-2 space-y-1" data-testid="smtp-test-steps">
          {result.steps.map((st) => (
            <li key={st.step} className="flex items-start gap-2 text-xs" data-testid={`smtp-step-${st.step}`} data-ok={st.ok}>
              {st.ok ? <CheckCircle className="h-3.5 w-3.5 text-[hsl(var(--primary))] mt-0.5 flex-shrink-0" /> : <XCircle className="h-3.5 w-3.5 text-destructive mt-0.5 flex-shrink-0" />}
              <span className={st.ok ? '' : 'font-semibold text-destructive'}>{st.label}</span>
              {st.detail && <span className="text-muted-foreground truncate">· {st.detail}</span>}
            </li>
          ))}
        </ol>
      )}
      {!ok && result.hints?.length > 0 && (
        <div className="mt-3 rounded-md bg-card border border-border p-2.5" data-testid="smtp-test-hints">
          <p className="text-xs font-semibold flex items-center gap-1 mb-1"><Lightbulb className="h-3.5 w-3.5 text-[hsl(var(--brand-apricot))]" /> Comment corriger</p>
          <ul className="list-disc pl-4 space-y-1 text-xs text-muted-foreground">{result.hints.map((h, i) => <li key={i}>{h}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

function EmailLogRow({ log }) {
  const Icon = log.status === 'sent' ? CheckCircle : XCircle;
  const iconCls = log.status === 'sent' ? 'text-[hsl(var(--primary))]' : 'text-destructive';
  return (
    <div className="flex items-start gap-2 text-xs border-b border-border/50 pb-1.5">
      <Icon className={`h-3.5 w-3.5 ${iconCls} mt-0.5 flex-shrink-0`} />
      <div className="min-w-0 flex-1">
        <div className="truncate"><span className="font-medium">{log.subject}</span>{log.to ? <span className="text-muted-foreground"> → {log.to}</span> : null}</div>
        {log.error && <div className="text-destructive truncate">{log.error}</div>}
      </div>
      <span className="text-muted-foreground whitespace-nowrap">{log.created_at ? new Date(log.created_at).toLocaleString('fr-FR') : ''}</span>
    </div>
  );
}

export function AdminSmtpCard({ adminKey, notificationEmail }) {
  const [settings, setSettings] = useState(EMPTY_SMTP);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [wasBroken, setWasBroken] = useState(false);
  const [health, setHealth] = useState(null);
  const [logs, setLogs] = useState([]);

  const loadHealth = useCallback(async () => {
    try {
      const res = await adminGet('/api/admin/smtp-status', adminKey);
      setHealth(res.data);
    } catch (err) {
      reportError('état des e-mails', err);
    }
  }, [adminKey]);

  const loadLogs = useCallback(async () => {
    try {
      const res = await adminGet('/api/admin/email-logs', adminKey, { limit: 20 });
      setLogs(res.data.items || []);
    } catch (err) {
      reportError('journal des emails', err);
    }
  }, [adminKey]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const smtp = await adminGet('/api/admin/smtp-settings', adminKey);
        if (!cancelled) setSettings({ ...EMPTY_SMTP, ...smtp.data, smtp_port: smtp.data.smtp_port || 587 });
      } catch (err) {
        reportError('paramètres SMTP', err);
      }
    })();
    loadLogs();
    loadHealth();
    return () => { cancelled = true; };
  }, [adminKey, loadLogs, loadHealth]);

  const save = async () => {
    try {
      await adminPut('/api/admin/smtp-settings', adminKey, settings);
      toast.success('Paramètres SMTP enregistrés');
    } catch (err) {
      reportError('enregistrement SMTP', err, (m) => toast.error(m || "Erreur lors de l'enregistrement"));
    }
  };

  const sendTest = async () => {
    setTesting(true); setTestResult(null);
    setWasBroken(health?.status === 'broken');
    try {
      const res = await adminPost('/api/admin/smtp-test', adminKey, { to_email: notificationEmail || '' });
      setTestResult(res.data);
      if (res.data.health) setHealth((h) => ({ ...(h || {}), ...res.data.health, configured: true }));
      if (res.data.success) toast.success(health?.status === 'broken' ? 'Rétabli : les e-mails partent de nouveau' : `Email de test envoyé à ${res.data.to}`);
      else toast.error(res.data.error);
    } catch (err) {
      const msg = reportError('test SMTP', err) || 'Erreur lors du test';
      setTestResult({ success: false, error: msg, to: '', explanation: 'Le test n\'a pas pu être lancé (limite de 20 tests par heure ou serveur indisponible).' });
      toast.error(msg);
    } finally {
      setTesting(false);
      loadLogs();
      loadHealth();
    }
  };

  const field = (key, label, props = {}) => (
    <div>
      <label className="text-xs font-medium block mb-1">{label}</label>
      <Input className="h-9" value={settings[key]} onChange={(e) => setSettings((p) => ({ ...p, [key]: e.target.value }))} {...props} />
    </div>
  );

  return (
    <>
      <Card className="p-5">
        <h3 className="font-['Space_Grotesk'] font-semibold mb-2">Paramètres SMTP (envoi d'emails)</h3>
        <p className="text-xs text-muted-foreground mb-3">Configurez votre serveur SMTP pour envoyer les notifications. Compatible Gmail, OVH, Ionos, etc.</p>
        <HealthBanner health={health} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          {field('smtp_host', 'Serveur SMTP', { placeholder: 'smtp.gmail.com' })}
          {field('smtp_port', 'Port', { type: 'number', placeholder: '587' })}
          {field('smtp_user', 'Utilisateur SMTP', { placeholder: 'votre@email.com' })}
          {field('smtp_pass', 'Mot de passe SMTP', { type: 'password', placeholder: '••••••••' })}
          {field('from_email', 'Email expéditeur', { placeholder: 'noreply@ptitservices.com' })}
        </div>
        <p className="text-[11px] text-muted-foreground mb-3">Port 465 = connexion SSL, port 587 = STARTTLS. Le mot de passe n'est pas ré-affiché pour des raisons de sécurité : saisissez-le à nouveau pour le modifier.</p>
        <div className="flex flex-wrap gap-2 items-center">
          <Button size="sm" onClick={save} data-testid="smtp-save-btn">Enregistrer les paramètres SMTP</Button>
          <Button size="sm" variant={health?.status === 'broken' ? 'default' : 'outline'} onClick={sendTest} disabled={testing} data-testid="smtp-test-btn">
            {testing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Stethoscope className="h-4 w-4 mr-1.5" />}{testing ? 'Diagnostic en cours…' : 'Tester et diagnostiquer l\'envoi'}
          </Button>
        </div>
        <TestResult result={testResult} wasBroken={wasBroken} />
      </Card>

      <Card className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-['Space_Grotesk'] font-semibold">Journal des emails envoyés</h3>
          <Button size="sm" variant="ghost" onClick={loadLogs} data-testid="email-logs-refresh-btn" aria-label="Actualiser"><RefreshCw className="h-4 w-4" /></Button>
        </div>
        {logs.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="email-logs-empty">Aucune tentative d'envoi enregistrée pour le moment.</p>
        ) : (
          <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="email-logs-list">
            {logs.map((log) => <EmailLogRow key={log._id} log={log} />)}
          </div>
        )}
      </Card>
    </>
  );
}
